import React, { useState, useEffect } from "react";
import LoadingComponent from "commons/loading/Loading.component";
import { getLanguageValue } from "locales/i18n";
import { PAGE_TYPE } from "enums";

const GrowthClass = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [classData, setClassData] = useState<any>(null);
    const [selectedTab, setSelectedTab] = useState('upcoming');

    // 더미 데이터
    const dummyClassData = {
        upcoming: [
            {
                id: 1,
                title: "보컬 레슨 - 고음 발성",
                instructor: "김보컬 선생님",
                date: "2024-03-18",
                time: "14:00-15:30",
                duration: "90분",
                type: "vocal",
                level: "중급",
                location: "1호실",
                description: "고음 발성 기법을 연습하고 안정화하는 수업입니다.",
                materials: ["보컬 워밍업 음악", "고음 연습곡"],
                maxStudents: 8,
                currentStudents: 6
            },
            {
                id: 2,
                title: "댄스 레슨 - 힙합 기본",
                instructor: "박댄스 선생님",
                date: "2024-03-19",
                time: "16:00-17:30",
                duration: "90분",
                type: "dance",
                level: "초급",
                location: "2호실",
                description: "힙합의 기본 동작과 리듬감을 익히는 수업입니다.",
                materials: ["운동복", "운동화"],
                maxStudents: 12,
                currentStudents: 10
            },
            {
                id: 3,
                title: "연기 레슨 - 감정 표현",
                instructor: "이연기 선생님",
                date: "2024-03-20",
                time: "19:00-20:30",
                duration: "90분",
                type: "acting",
                level: "중급",
                location: "3호실",
                description: "다양한 감정을 표현하는 연기 기법을 배우는 수업입니다.",
                materials: ["연기 대본", "거울"],
                maxStudents: 6,
                currentStudents: 4
            }
        ],
        completed: [
            {
                id: 4,
                title: "보컬 레슨 - 호흡법",
                instructor: "김보컬 선생님",
                date: "2024-03-15",
                time: "14:00-15:30",
                duration: "90분",
                type: "vocal",
                level: "중급",
                location: "1호실",
                description: "올바른 호흡법과 지지음을 연습했습니다.",
                materials: ["호흡 연습 도구"],
                maxStudents: 8,
                currentStudents: 8,
                attendance: true,
                score: 8.5,
                feedback: "호흡법이 많이 개선되었습니다. 계속 연습하세요!"
            },
            {
                id: 5,
                title: "댄스 레슨 - 팝핀",
                instructor: "박댄스 선생님",
                date: "2024-03-14",
                time: "16:00-17:30",
                duration: "90분",
                type: "dance",
                level: "초급",
                location: "2호실",
                description: "팝핀의 기본 동작을 배웠습니다.",
                materials: ["운동복", "운동화"],
                maxStudents: 12,
                currentStudents: 11,
                attendance: true,
                score: 7.8,
                feedback: "기본 동작이 좋습니다. 더 정확한 동작을 연습하세요."
            }
        ],
        schedule: [
            {
                day: "월",
                date: "2024-03-18",
                classes: [
                    { time: "14:00-15:30", title: "보컬 레슨", instructor: "김보컬", type: "vocal" }
                ]
            },
            {
                day: "화",
                date: "2024-03-19",
                classes: [
                    { time: "16:00-17:30", title: "댄스 레슨", instructor: "박댄스", type: "dance" }
                ]
            },
            {
                day: "수",
                date: "2024-03-20",
                classes: [
                    { time: "19:00-20:30", title: "연기 레슨", instructor: "이연기", type: "acting" }
                ]
            },
            {
                day: "목",
                date: "2024-03-21",
                classes: []
            },
            {
                day: "금",
                date: "2024-03-22",
                classes: [
                    { time: "15:00-16:30", title: "보컬 레슨", instructor: "김보컬", type: "vocal" }
                ]
            },
            {
                day: "토",
                date: "2024-03-23",
                classes: [
                    { time: "10:00-11:30", title: "댄스 레슨", instructor: "박댄스", type: "dance" }
                ]
            },
            {
                day: "일",
                date: "2024-03-24",
                classes: []
            }
        ]
    };

    useEffect(() => {
        // 더미 데이터 로딩 시뮬레이션
        setIsLoading(true);
        setTimeout(() => {
            setClassData(dummyClassData);
            setIsLoading(false);
        }, 1000);
    }, []);

    const getTypeColor = (type: string) => {
        switch (type) {
            case 'vocal':
                return 'bg-blue-100 text-blue-800';
            case 'dance':
                return 'bg-green-100 text-green-800';
            case 'acting':
                return 'bg-purple-100 text-purple-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    const getTypeIcon = (type: string) => {
        switch (type) {
            case 'vocal':
                return '🎤';
            case 'dance':
                return '💃';
            case 'acting':
                return '🎭';
            default:
                return '📚';
        }
    };

    if (isLoading || !classData) {
        return <LoadingComponent />;
    }

    return (
        <main className="bg-gray-50 min-h-screen">
            <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-6">
                {/* 헤더 */}
                <div className="mb-8">
                    <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-gray-900 mb-2">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtTitle")}
                    </h1>
                    <p className="text-gray-600 text-sm sm:text-base">
                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtDesc")}
                    </p>
                </div>

                {/* 탭 네비게이션 */}
                <div className="bg-white rounded-2xl shadow-sm p-1 mb-8">
                    <div className="flex">
                        <button
                            onClick={() => setSelectedTab('upcoming')}
                            className={`flex-1 px-4 py-3 text-sm sm:text-base font-medium rounded-xl transition-all duration-200 ${
                                selectedTab === 'upcoming'
                                    ? 'bg-[#CF3688] text-white shadow-lg'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                        >
                            {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtUpcoming")}
                        </button>
                        <button
                            onClick={() => setSelectedTab('completed')}
                            className={`flex-1 px-4 py-3 text-sm sm:text-base font-medium rounded-xl transition-all duration-200 ${
                                selectedTab === 'completed'
                                    ? 'bg-[#CF3688] text-white shadow-lg'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'
                            }`}
                        >
                            {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtCompleted")}
                        </button>
                        <button
                            onClick={() => setSelectedTab('schedule')}
                            className={`flex-1 px-4 py-3 text-sm sm:text-base font-medium rounded-xl transition-all duration-200 ${
                                selectedTab === 'schedule'
                                    ? 'bg-[#CF3688] text-white shadow-lg'
                                    : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                            }`}
                        >
                            {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtSchedule")}
                        </button>
                    </div>
                </div>

                {/* 수업 목록 */}
                {selectedTab === 'upcoming' && (
                    <div className="space-y-6">
                        {classData?.upcoming?.map((classItem: any) => (
                            <div key={classItem.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                                <div className="p-4 sm:p-6">
                                    <div className="flex flex-col lg:flex-row lg:items-start gap-4 sm:gap-6">
                                        {/* 왼쪽 - 수업 정보 */}
                                        <div className="flex-1">
                                            <div className="flex items-start gap-3 mb-3">
                                                <span className="text-2xl sm:text-3xl">
                                                    {getTypeIcon(classItem.type)}
                                                </span>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(classItem.type)}`}>
                                                            {getLanguageValue(PAGE_TYPE.STUDENT, `growthClass.type.${classItem.type}`)}
                                                        </span>
                                                        <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700">
                                                            {classItem.level}
                                                        </span>
                                                    </div>
                                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                                                        {classItem.title}
                                                    </h3>
                                                    <p className="text-sm text-gray-600 mb-3">
                                                        {classItem.description}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* 수업 세부 정보 */}
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                                                <div className="text-center p-3 bg-gray-50 rounded-xl">
                                                    <p className="text-xs text-gray-500 mb-1">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtInstructor")}
                                                    </p>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {classItem.instructor}
                                                    </p>
                                                </div>
                                                <div className="text-center p-3 bg-gray-50 rounded-xl">
                                                    <p className="text-xs text-gray-500 mb-1">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtDate")}
                                                    </p>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {classItem.date}
                                                    </p>
                                                </div>
                                                <div className="text-center p-3 bg-gray-50 rounded-xl">
                                                    <p className="text-xs text-gray-500 mb-1">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtTime")}
                                                    </p>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {classItem.time}
                                                    </p>
                                                </div>
                                                <div className="text-center p-3 bg-gray-50 rounded-xl">
                                                    <p className="text-xs text-gray-500 mb-1">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtLocation")}
                                                    </p>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {classItem.location}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* 준비물 */}
                                            <div className="mb-4">
                                                <h4 className="text-sm font-semibold text-gray-900 mb-2">
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtMaterials")}
                                                </h4>
                                                <div className="flex flex-wrap gap-2">
                                                    {classItem.materials.map((material: string, index: number) => (
                                                        <span key={index} className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                                            {material}
                                                        </span>
                                                    ))}
                                                </div>
                                            </div>
                                        </div>

                                        {/* 오른쪽 - 수강 인원 및 버튼 */}
                                        <div className="lg:w-48 flex flex-col items-center gap-4">
                                            <div className="text-center p-4 bg-blue-50 rounded-xl w-full">
                                                <p className="text-sm text-blue-600 mb-1">
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtEnrollment")}
                                                </p>
                                                <p className="text-2xl font-bold text-blue-600">
                                                    {classItem.currentStudents}/{classItem.maxStudents}
                                                </p>
                                                <div className="w-full bg-blue-200 rounded-full h-2 mt-2">
                                                    <div 
                                                        className="bg-blue-500 h-2 rounded-full transition-all duration-300"
                                                        style={{ width: `${(classItem.currentStudents / classItem.maxStudents) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </div>
                                            <button className="w-full bg-purple-500 text-white py-3 px-4 rounded-xl font-medium hover:bg-purple-600 transition-colors duration-200">
                                                {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtJoin")}
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* 완료된 수업 */}
                {selectedTab === 'completed' && (
                    <div className="space-y-6">
                        {classData?.completed?.map((classItem: any) => (
                            <div key={classItem.id} className="bg-white rounded-2xl shadow-sm overflow-hidden">
                                <div className="p-4 sm:p-6">
                                    <div className="flex flex-col lg:flex-row lg:items-start gap-4 sm:gap-6">
                                        {/* 왼쪽 - 수업 정보 */}
                                        <div className="flex-1">
                                            <div className="flex items-start gap-3 mb-3">
                                                <span className="text-2xl sm:text-3xl">
                                                    {getTypeIcon(classItem.type)}
                                                </span>
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getTypeColor(classItem.type)}`}>
                                                            {getLanguageValue(PAGE_TYPE.STUDENT, `growthClass.type.${classItem.type}`)}
                                                        </span>
                                                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                                                            classItem.attendance ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                                                        }`}>
                                                            {classItem.attendance 
                                                                ? getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtAttended")
                                                                : getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtAbsent")
                                                            }
                                                        </span>
                                                    </div>
                                                    <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-2">
                                                        {classItem.title}
                                                    </h3>
                                                    <p className="text-sm text-gray-600 mb-3">
                                                        {classItem.description}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* 수업 세부 정보 */}
                                            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mb-4">
                                                <div className="text-center p-3 bg-gray-50 rounded-xl">
                                                    <p className="text-xs text-gray-500 mb-1">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtInstructor")}
                                                    </p>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {classItem.instructor}
                                                    </p>
                                                </div>
                                                <div className="text-center p-3 bg-gray-50 rounded-xl">
                                                    <p className="text-xs text-gray-500 mb-1">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtDate")}
                                                    </p>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {classItem.date}
                                                    </p>
                                                </div>
                                                <div className="text-center p-3 bg-gray-50 rounded-xl">
                                                    <p className="text-xs text-gray-500 mb-1">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtScore")}
                                                    </p>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {classItem.score}/10
                                                    </p>
                                                </div>
                                                <div className="text-center p-3 bg-gray-50 rounded-xl">
                                                    <p className="text-xs text-gray-500 mb-1">
                                                        {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtLocation")}
                                                    </p>
                                                    <p className="text-sm font-medium text-gray-900">
                                                        {classItem.location}
                                                    </p>
                                                </div>
                                            </div>

                                            {/* 피드백 */}
                                            <div className="bg-gray-50 rounded-xl p-4">
                                                <h4 className="text-sm font-semibold text-gray-800 mb-2">
                                                    {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtFeedback")}
                                                </h4>
                                                <p className="text-sm text-gray-700">
                                                    {classItem.feedback}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                )}

                {/* 주간 스케줄 */}
                {selectedTab === 'schedule' && (
                    <div className="bg-white rounded-2xl shadow-sm p-4 sm:p-6">
                        <h2 className="text-xl sm:text-2xl font-bold text-gray-900 mb-4 sm:mb-6">
                            {getLanguageValue(PAGE_TYPE.STUDENT, "growthClass.txtWeeklySchedule")}
                        </h2>
                        <div className="grid grid-cols-7 gap-2 sm:gap-4">
                            {classData?.schedule?.map((day: any, index: number) => (
                                <div key={index} className="text-center">
                                    <div className="p-2 sm:p-3 bg-gray-50 rounded-xl mb-2">
                                        <p className="text-sm font-medium text-gray-900 mb-1">
                                            {day.day}
                                        </p>
                                        <p className="text-xs text-gray-500">
                                            {day.date.split('-')[2]}
                                        </p>
                                    </div>
                                    <div className="space-y-2">
                                        {day.classes.map((classItem: any, classIndex: number) => (
                                            <div key={classIndex} className="p-2 bg-gray-50 rounded-lg">
                                                <p className="text-xs font-medium text-gray-800 mb-1">
                                                    {classItem.time}
                                                </p>
                                                <p className="text-xs text-gray-700 truncate">
                                                    {classItem.title}
                                                </p>
                                                <p className="text-xs text-gray-600">
                                                    {classItem.instructor}
                                                </p>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                )}

                {/* 새 수업 신청 버튼 */}
                <div className="fixed bottom-6 right-6 sm:bottom-8 sm:right-8">
                    <button className="w-14 h-14 sm:w-16 sm:h-16 bg-[#CF3688] text-white rounded-full shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-110 flex items-center justify-center">
                        <svg className="w-6 h-6 sm:w-7 sm:h-7" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 4v16m8-8H4" />
                        </svg>
                    </button>
                </div>
            </div>
        </main>
    );
};

export default GrowthClass;
